import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, Tabs, Input, Pagination, Select } from 'antd';
import { FilterOutlined, CarOutlined, CalendarOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import config from '../../../config';
import LivraisonClientDetail from './livraisonClientDetail/LivraisonClientDetail';
import { useSelector } from 'react-redux';
import Livraison_detailSelect from './Livraison_detailSelect';
import LivraisonJour from '../LivraisonJour';
import LivraisonView from '../livraisonView/LivraisonView';
import {
  NdoePage, NdoePageHeader, NdoeToolbar, NdoeActionButton, NdoeResponsiveTable,
} from '../../../components/ndoe';
import { buildLivraisonColumns, renderLivraisonCard, LIVRAISON_STATUT_OPTIONS } from '../livraisonShared';
import { subscribeRealtime } from '../../../realtime';

const Livraison_detail = () => {
    const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const startDate = queryParams.get('start_date');
    const endDate = queryParams.get('end_date');
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [open, setOpen] = useState(false);
    const [opens, setOpens] = useState(false);
    const [openInfo, setOpenInfo] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [idClient, setIdClient] = useState({});
    const [id_commande, setId_commande] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15 });
    const [totalItems, setTotalItems] = useState('');
    const [statusSel, setStatusSel] = useState([]); // ⑨ filtre multi-statuts (serveur)
    const user = useSelector((state) => state.user?.currentUser);
    const isAdmin = user?.role === 'admin';
    const [flashIds, setFlashIds] = useState(() => new Set()); // commandes qui viennent de bouger (surlignage)
    const fetchRef = useRef(null); // pointe vers le dernier fetchData (pour le flux temps réel)
    const opensRef = useRef(false); // filtre par dates custom actif : on suspend le refetch live

    const fetchData = async (page = currentPage, size = pageSize) => {
      try {
        const statusParam = statusSel.length ? `&status=${statusSel.join(',')}` : '';
        const { data } = await axios.get(`${DOMAIN}/api/livraison/livraisonDetail?start_date=${startDate}&end_date=${endDate}&page=${page}&pageSize=${size}${statusParam}`);
        setData(data.data);
        setTotalItems(data.total);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    // ⑨ Changement de statuts : on repart en page 1 (le total change).
    const onStatusChange = (vals) => { setStatusSel(vals); setCurrentPage(1); };

    useEffect(() => {
      fetchData(currentPage, pageSize);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, statusSel]);

    // Temps réel (R2) : à l'arrivée d'un événement « livraison » (planification,
    // prise en charge, résultat, réaffectation…), on rafraîchit la page courante
    // (débounce anti-rafale) et on surligne brièvement les lignes concernées.
    fetchRef.current = () => fetchData(currentPage, pageSize);
    opensRef.current = opens;
    useEffect(() => {
      let debounce = null;
      const unsub = subscribeRealtime((payload) => {
        // Un filtre par dates personnalisé est ouvert : ne pas l'écraser avec un
        // refetch de la plage par défaut déclenché par un événement distant.
        if (opensRef.current) return;
        const ids = (payload?.notifications || [])
          .filter((n) => n.famille === 'livraisons' && n.id_commande)
          .map((n) => Number(n.id_commande));
        if (!ids.length) return;
        setFlashIds((prev) => { const s = new Set(prev); ids.forEach((id) => s.add(id)); return s; });
        clearTimeout(debounce);
        debounce = setTimeout(() => { if (fetchRef.current) fetchRef.current(); }, 600);
        setTimeout(() => {
          setFlashIds((prev) => { const s = new Set(prev); ids.forEach((id) => s.delete(id)); return s; });
        }, 6000);
      });
      return () => { clearTimeout(debounce); unsub(); };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDelete = async (id) => {
      try {
        await axios.delete(`${DOMAIN}/api/livraison/livraisonDeleteDetail/${id}`);
        fetchData(currentPage, pageSize);   // refetch au lieu de recharger toute la page
      } catch (err) {
        console.log(err);
      }
    };

    const handleTableChange = (paginationArg) => {
      // Pas de fetchData explicite ici : le useEffect [currentPage,pageSize,statusSel]
      // s'en charge (l'appel explicite doublait chaque requête au changement de page).
      setCurrentPage(paginationArg.current);
      setPageSize(paginationArg.pageSize);
      setPagination({ current: paginationArg.current, pageSize: paginationArg.pageSize });
    };

    const showClient = (id) => { setIdClient(id); setOpen(true); };
    const handInfo = (id) => { setId_commande(id); setOpenInfo(true); };

    /*
      OUVRIR DIRECTEMENT LA LIVRAISON DONT LA NOTIFICATION PARLE.

      Signalé le 21/08/2026 : « lorsque je clique sur la notification résultat
      de la livraison, je dois voir une vue de ce résultat là détaillé ». Le
      tap menait à CET écran — la liste — et il fallait y retrouver la bonne
      ligne à la main. La notification porte désormais `?commande=N`.

      On dépend de `location.search` et non d'un état interne : revenir sur
      l'écran depuis une AUTRE notification doit rouvrir la nouvelle commande,
      pas garder la précédente. Un montage unique ne le ferait pas.
    */
    useEffect(() => {
      const cible = new URLSearchParams(location.search).get('commande');
      const n = Number(cible);
      if (Number.isFinite(n) && n > 0) handInfo(n);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const columns = buildLivraisonColumns({
      onClient: showClient, onView: handInfo, onDelete: handleDelete, isAdmin, communeTone: 'red',
      indexOffset: ((pagination.current || 1) - 1) * (pagination.pageSize || 15),
    });

    const filteredData = data?.filter((item) =>
      item.nom_client?.toLowerCase().includes(searchValue.toLowerCase()) ||
      item.nom_commune?.toLowerCase().includes(searchValue.toLowerCase()) ||
      item.nom_livreur?.toLowerCase().includes(searchValue.toLowerCase())
    );

    const tabListe = (
      <>
        <NdoeToolbar
          search={
            <Input.Search
              type="search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Rechercher (client, commune, livreur)…"
              allowClear
              style={{width:'250px'}}
            />
          }
        >
          <Select
            mode="multiple"
            allowClear
            placeholder="Filtrer par statut"
            value={statusSel}
            onChange={onStatusChange}
            options={LIVRAISON_STATUT_OPTIONS}
            style={{ minWidth: 200 }}
            maxTagCount="responsive"
          />
          <NdoeActionButton variant={opens ? 'ink' : 'secondary'} icon={<FilterOutlined />} onClick={() => setOpens((o) => !o)}>
            Filtrer par dates
          </NdoeActionButton>
        </NdoeToolbar>

        {opens && (
          <div className="ndoe-card" style={{ marginBottom: 16 }}>
            <Livraison_detailSelect getProduits={setData} />
          </div>
        )}

        <NdoeResponsiveTable
          data={filteredData}
          columns={columns}
          rowKey="id_detail_livraison"
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{ current: currentPage, pageSize, total: Number(totalItems) || 0 }}
          onChange={handleTableChange}
          onRow={(record) => ({ className: flashIds.has(Number(record.id_commande)) ? 'ndoe-row-flash' : '' })}
          renderCard={(record) => renderLivraisonCard(record, { onView: handInfo, onDelete: handleDelete, isAdmin })}
          empty={{ icon: '🚚', title: 'Aucune livraison', text: 'Ajustez la recherche ou la période.' }}
          mobilePager={
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
              <Pagination current={currentPage} pageSize={pageSize} total={Number(totalItems) || 0} showSizeChanger={false}
                onChange={(p, s) => handleTableChange({ current: p, pageSize: s })} />
            </div>
          }
        />
      </>
    );

  const tabItems = [
    {
      key: "0",
      label: (
        <>
          <CarOutlined />Livraisons
        </>
      ),
      children: tabListe,
    },
    {
      key: "1",
      label: (
        <>
          <CalendarOutlined />Livraisons du jour
        </>
      ),
      children: <LivraisonJour />,
    },
  ];

  return (
    <NdoePage>
      <NdoePageHeader eyebrow="Logistique" title="Détails de livraison" subtitle="Superviser les livraisons et leur affectation" />

      <Tabs type="card" items={tabItems} />

      <Modal title="Information du client" centered open={open} onCancel={() => setOpen(false)} width={1150}
        footer={[<Button key="annuler" onClick={() => setOpen(false)}>Annuler</Button>]}>
        <LivraisonClientDetail idClients={idClient} />
      </Modal>

      <Modal title="" centered open={openInfo} onCancel={() => setOpenInfo(false)} width={1200} footer={[]}>
        <LivraisonView id={id_commande} />
      </Modal>
    </NdoePage>
  )
}

export default Livraison_detail
